import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import userStore from '../store/userStore';

/**
 * El chat de ayuda del cliente: dudas del producto, problemas, sugerencias
 * (docs/chat-soporte-plan.md, 28/10/2026). Va por Crisp y lo contesta ClaimUp a
 * mano, desde la bandeja de Crisp o su app de móvil.
 *
 * Solo para el cliente (empresa, su equipo y el cliente de recobro): el admin y
 * el despacho no lo ven. Lo de un expediente concreto no va aquí, sino en una
 * anotación «para ClaimUp» dentro del expediente.
 *
 * El ID de la web en Crisp no es secreto: va en el código de cualquier web que
 * lo use. Se puede cambiar con REACT_APP_CRISP_WEBSITE_ID.
 */
export const CRISP_WEBSITE_ID = process.env.REACT_APP_CRISP_WEBSITE_ID || '3c596f18-ba4d-4d64-bd19-1a24794cbcef';
export const SUPPORT_CHAT_ROLES = ['company', 'gestor', 'operator', 'client'];

const crisp = (...cmd) => {
  window.$crisp = window.$crisp || [];
  window.$crisp.push(cmd);
};

const cargarScript = () => {
  if (document.getElementById('crisp-chat-script')) return;
  window.$crisp = window.$crisp || [];
  window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;
  const s = document.createElement('script');
  s.id = 'crisp-chat-script';
  s.src = 'https://client.crisp.chat/l.js';
  s.async = true;
  document.head.appendChild(s);
};

const SupportChat = () => {
  const user = userStore(state => state.user);
  const token = userStore(state => state.token);
  const { pathname } = useLocation();
  const identificado = useRef(null);
  const toca = Boolean(token) && SUPPORT_CHAT_ROLES.includes(user?.role);

  // Cargarlo y decir quién escribe, para que en la bandeja sepáis de quién es.
  useEffect(() => {
    if (!toca) {
      // Al salir, o si entra alguien que no lo ve: fuera la conversación anterior.
      if (identificado.current) {
        crisp('do', 'session:reset');
        crisp('do', 'chat:hide');
        identificado.current = null;
      }
      return;
    }
    cargarScript();
    crisp('do', 'chat:show');
    if (identificado.current === user.id) return;
    identificado.current = user.id;

    const empresaId = user.role === 'company' || user.role === 'client' ? user.id : user.parent_id;
    crisp('set', 'session:data', [
      [
        ['rol', user.role],
        ['usuario_id', String(user.id)],
        ['empresa_id', String(empresaId || '')],
      ],
    ]);
    crisp('set', 'session:segments', [[user.role === 'client' ? 'cliente-recobro' : 'cliente-ar']]);

    api
      .get(`/users/${user.id}`)
      .then(({ data }) => {
        const nombre = [data?.username, data?.userlastname].filter(Boolean).join(' ').trim();
        if (data?.usermail) crisp('set', 'user:email', [data.usermail]);
        if (nombre) crisp('set', 'user:nickname', [nombre]);
        const empresa = data?.nombre_comercial || (user.role === 'company' ? nombre : '');
        if (empresa) crisp('set', 'user:company', [empresa]);
      })
      .catch(() => {});
  }, [toca, user?.id, user?.role, user?.parent_id]);

  // En qué pantalla estaba al escribir: «no me sale» se entiende mejor así.
  useEffect(() => {
    if (toca) crisp('set', 'session:data', [[['pantalla', pathname]]]);
  }, [toca, pathname]);

  return null;
};

export default SupportChat;
